// Manifest for the Piggington vector sprite (design handoff, Aug 2026 — public/piggington-sprite.svg).
// Authored as a plain .js module rather than imported JSON, matching every other content
// module in this codebase (STARTER_VOCAB_LISTS, SPELLING_LISTS, etc.) — import-free, no
// build-step JSON-loader dependency. Values are transcribed unaltered from the handoff.
//
// Aug 2026 wardrobe expansion: the handoff's second drop added 12 cosmetics (hair,
// eyebrows, eyelashes, headphones, ear muffs, coat, jumper, shorts, pyjamas, slippers,
// socks, wooly hat) and four new slots (brows, lashes, ears, outfit). The sprite now holds
// 31 symbols (1 base + 7 expressions + 21 handoff cosmetics + our 2 hand-drawn extras).
// All pre-existing symbols are byte-identical to the previous sprite — the update is purely
// additive, so nothing a child already owns can render differently.
//
// All symbols share one 200×260 coordinate space. Render order is always:
// base -> expression -> cosmetics ascending by z. See the <Piggington> component in
// Common.jsx for the actual render.

export const PIGGINGTON_VIEWBOX = "0 0 200 260";
export const PIGGINGTON_SPRITE_SRC = "/piggington-sprite.svg";

export const PIGGINGTON_PALETTE = {
  yellow: "#FFC629",
  navy: "#1b2a4a",
  blush: "#F5A3A8",
  coin: "#FFE39B",
  cream: "#FFFDF6",
};

export const PIGGINGTON_BASE = { id: "pg-base", z: 0 };

// use: a short note on the moment each expression is meant for — informs (but doesn't
// hard-code) the mood-selection logic in PigCard (ChildView.jsx).
export const PIGGINGTON_EXPRESSIONS = [
  { id: "fx-neutral", z: 10, label: "Neutral", use: "default / idle" },
  { id: "fx-happy", z: 10, label: "Happy", use: "deposit made, streak kept" },
  { id: "fx-wow", z: 10, label: "Wow", use: "new goal, big milestone" },
  { id: "fx-thinking", z: 10, label: "Thinking", use: "quiz / choice screens" },
  { id: "fx-sleeping", z: 10, label: "Sleeping", use: "empty state, no activity" },
  { id: "fx-oops", z: 10, label: "Oops", use: "overspend, failed transfer" },
  { id: "fx-celebrating", z: 10, label: "Celebrating", use: "goal reached, reward unlocked" },
];

// slot: one of head/eyes/brows/lashes/ears/neck/torso/outfit/legs/feet/hands/held — enforce
// one-at-a-time per slot in any picker UI (see slotRules below). hidesCoinSlot flags
// cos-tee specifically (per the handoff manifest): it covers the coin-slot mark on the
// belly, which is fine for dress-up but not for a saving-flow screen where that mark
// matters. cos-pyjamas is a ONE-PIECE "outfit" slot item — it covers torso + legs and must
// never be combined with torso OR legs items (enforced in lib/oinks.js's applyEquip, not
// just the picker). cos-hair shares the "head" slot with the hats — hair xor hat, per the
// manifest. cos-baseball-cap and cos-crown are NOT part of the design handoff — hand-drawn
// (2026-08) to match its established geometry conventions (same head anchor as
// cos-bowler/cos-party-hat, stroke-width 7, navy #1b2a4a stroke) per Alan's request; they
// were carried forward into the expanded sprite because children already own them.
//
// Aug 2026 characters/economy task bugfix: 11 entries below (cos-gloves through
// cos-held-bank) were added to OINK_CATALOG (lib/oinks.js) by that task but never added
// HERE — this is the array <Piggington> (Common.jsx) actually filters against to decide
// what to draw (`PIGGINGTON_COSMETICS.filter(c => cosmetics.includes(c.id))`); an id
// missing from this list simply never gets a <use> element, silently. That PR also carried
// a second, unrelated bug into the shop tile CSS — see ChildView.jsx's OinkShop comment on
// why cos-coin (already present here, unchanged) went invisible too. z/slot/label values
// below are transcribed verbatim from docs/piggington-manifest.json, not invented — held
// and legs are real distinct slots there, not folded into hands/torso as OINK_CATALOG's
// characters-PR draft incorrectly simplified them to (also fixed, see oinks.js).
export const PIGGINGTON_COSMETICS = [
  { id: "cos-socks", z: 4, slot: "feet", label: "Socks" },
  { id: "cos-shoes", z: 5, slot: "feet", label: "Shoes" },
  { id: "cos-slippers", z: 5, slot: "feet", label: "Slippers" },
  { id: "cos-hair", z: 6, slot: "head", label: "Hair" },
  { id: "cos-hair-long-blonde", z: 7, slot: "head", label: "Long hair, blonde" },
  { id: "cos-hair-long-brown", z: 7, slot: "head", label: "Long hair, brown" },
  { id: "cos-eyebrows", z: 12, slot: "brows", label: "Eyebrows" },
  { id: "cos-eyelashes", z: 13, slot: "lashes", label: "Eyelashes" },
  { id: "cos-tee", z: 20, slot: "torso", label: "T-shirt", hidesCoinSlot: true },
  { id: "cos-shorts", z: 21, slot: "torso", label: "Shorts" },
  { id: "cos-coat", z: 22, slot: "torso", label: "Coat" },
  { id: "cos-jumper", z: 22, slot: "torso", label: "Jumper" },
  { id: "cos-pyjamas", z: 23, slot: "outfit", label: "Pyjamas", note: "one-piece; covers torso and legs, use alone" },
  { id: "cos-dress", z: 23, slot: "outfit", label: "Dress", note: "has its own sleeves; excludes torso items" },
  { id: "cos-skirt", z: 24, slot: "legs", label: "Skirt", note: "layers over a torso garment" },
  { id: "cos-tie", z: 25, slot: "neck", label: "Tie" },
  { id: "cos-bow-tie", z: 25, slot: "neck", label: "Bow tie" },
  { id: "cos-glasses", z: 30, slot: "eyes", label: "Glasses" },
  { id: "cos-monocle", z: 30, slot: "eyes", label: "Monocle" },
  { id: "cos-coin", z: 35, slot: "hands", label: "Coin" },
  { id: "cos-gloves", z: 36, slot: "hands", label: "Gloves" },
  { id: "cos-mittens", z: 36, slot: "hands", label: "Mittens" },
  { id: "cos-held-bank", z: 37, slot: "held", label: "Piggy bank" },
  { id: "cos-held-umbrella", z: 37, slot: "held", label: "Umbrella" },
  { id: "cos-held-balloon", z: 37, slot: "held", label: "Balloon" },
  { id: "cos-held-book", z: 37, slot: "held", label: "Book" },
  { id: "cos-bowler", z: 40, slot: "head", label: "Bowler hat" },
  { id: "cos-party-hat", z: 40, slot: "head", label: "Party hat" },
  { id: "cos-wooly-hat", z: 40, slot: "head", label: "Wooly hat" },
  { id: "cos-baseball-cap", z: 40, slot: "head", label: "Baseball cap" },
  { id: "cos-crown", z: 40, slot: "head", label: "Crown" },
  { id: "cos-mohawk", z: 41, slot: "head", label: "Mohawk" },
  { id: "cos-headphones", z: 45, slot: "ears", label: "Headphones" },
  { id: "cos-ear-muffs", z: 45, slot: "ears", label: "Ear muffs" },
];

export const PIGGINGTON_SLOT_RULES = {
  head: "one at a time — hats or hair",
  eyes: "one at a time; sits over the expression",
  brows: "one at a time",
  lashes: "one at a time",
  ears: "one at a time",
  neck: "one at a time",
  torso: "one at a time; not combined with outfit",
  outfit: "one-piece garments (pyjamas, dress) — covers torso + legs, excludes torso and legs items",
  legs: "one at a time (skirt) — layers over a torso garment, not over an outfit",
  feet: "one at a time — shoes, slippers or socks",
  hands: "one at a time — gloves/mittens cover both paws",
  held: "one at a time — an object in the right paw; can be worn together with hands",
};
